import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../utils/format';
import ErrorLoading from '../components/ErrorLoading';

/**
 * One Click Filter —「相似盤路」歷史比對頁（Freedom 2026-08-30 需求）
 *
 * 從 SG Pools 列表點「One Click」進來，比對條件：
 *   開盤讓球 first_home_handicap / 收盤讓球 home_handicap / 主客賠率
 * 後端樣本不足時會逐級放寬，match_level 會標示實際用到哪一層。
 */
function SimilarEvents({ token, backendUrl }) {
  const { t } = useTranslation();
  const { dateEventId } = useParams();
  const [data, setData] = useState(null);
  // null = 讓後端自動逐級放寬；有值 = 使用者手動指定要看哪一層
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${backendUrl}/sg_pools/events/${dateEventId}/similar`,
        { headers: { Authorization: `Bearer ${token}` }, params: level ? { level } : {} }
      );
      if (res.data?.error) setError(res.data.error);
      else setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, dateEventId, token, level]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const outcome = (m) => {
    const adj = m.margin + m.close;
    if (adj > 0.25) return { label: t('similar.homeCovered'), cls: 'text-success' };
    if (adj === 0.25) return { label: t('similar.homeHalf'), cls: 'text-success' };
    if (adj === 0) return { label: t('similar.push'), cls: 'text-secondary' };
    if (adj === -0.25) return { label: t('similar.awayHalf'), cls: 'text-danger' };
    return { label: t('similar.awayCovered'), cls: 'text-danger' };
  };

  const s = data?.summary;
  const tgt = data?.target;

  return (
    <div className="container-fluid p-3">
      <h4>{t('similar.title')}</h4>

      {tgt && (
        <div className="alert alert-secondary py-2">
          <strong>{tgt.name}</strong>
          <span className="ms-3">{formatDateTime(tgt.start_time)}</span>
          <div className="small mt-1">
            {t('sgPools.firstHomeHandicap')}: <strong>{tgt.first_home_handicap}</strong>
            {' → '}
            {t('sgPools.homeHandicap')}: <strong>{tgt.home_handicap}</strong>
            {'　|　'}
            {t('sgPools.homeOdds')}: <strong>{tgt.home_price}</strong>
            {' / '}
            {t('sgPools.awayOdds')}: <strong>{tgt.away_price}</strong>
          </div>
        </div>
      )}

      <ErrorLoading error={error} loading={loading} />

      {data && !loading && (
        <>
          <div className="mb-2">
            <span className="badge bg-info text-dark me-2">{t('similar.matchLevel')}: {data.match_level}</span>
            <span className="ms-2 small">{
              t(`similar.levelDesc.${data.match_level}`) === `similar.levelDesc.${data.match_level}`
                ? data.match_level_desc
                : t(`similar.levelDesc.${data.match_level}`)
            }</span>
            <div className="small text-muted mt-2">
              {t('similar.tierCounts')}:{' '}
              {Object.entries(data.tier_counts || {}).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  className={`btn btn-sm py-0 me-1 ${
                    k === data.match_level ? 'btn-primary' : 'btn-outline-secondary'
                  }`}
                  disabled={v === 0}
                  onClick={() => setLevel(k)}
                >
                  {k} {v}
                </button>
              ))}
              {level && (
                <button
                  type="button"
                  className="btn btn-sm btn-link py-0"
                  onClick={() => setLevel(null)}
                >
                  {t('similar.autoLevel')}
                </button>
              )}
            </div>
          </div>

          {s?.n > 0 ? (
            <>
              {!s.reliable && (
                <div className="alert alert-warning py-2 small">
                  {t('similar.smallSample', { n: s.n })}
                </div>
              )}
              <div className="row g-2 mb-3">
                <div className="col-md-3">
                  <div className="card p-2">
                    <div className="small text-muted">{t('similar.sample')}</div>
                    <div className="h5 mb-0">{s.n}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card p-2">
                    <div className="small text-muted">{t('similar.hda')}</div>
                    <div className="h5 mb-0">{s.home_win_pct}% / {s.draw_pct}% / {s.away_win_pct}%</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card p-2">
                    <div className="small text-muted">{t('similar.homeSide')}</div>
                    <div className="h5 mb-0">
                      {s.home.cover_pct}%
                      <span className={`ms-2 small ${s.home.roi_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                        ROI {s.home.roi_pct}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card p-2">
                    <div className="small text-muted">{t('similar.awaySide')}</div>
                    <div className="h5 mb-0">
                      {s.away.cover_pct}%
                      <span className={`ms-2 small ${s.away.roi_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                        ROI {s.away.roi_pct}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="small text-muted mb-2">
                {t('similar.over25')}: {s.over_2_5_pct}%　|　{t('similar.avgGoals')}: {s.avg_goals}
                　|　{t('similar.roiNote')}
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>{t('sgPools.startTime')}</th>
                      <th>{t('sgPools.name')}</th>
                      <th>{t('sgPools.firstHomeHandicap')}</th>
                      <th>{t('sgPools.homeHandicap')}</th>
                      <th>{t('sgPools.homeOdds')}</th>
                      <th>{t('sgPools.awayOdds')}</th>
                      <th>{t('sgPools.halfScore')}</th>
                      <th>{t('sgPools.fullScore')}</th>
                      <th>{t('similar.result')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.matches.map((m) => {
                      const o = outcome(m);
                      return (
                        <tr key={m.date_event_id}>
                          <td>{formatDateTime(m.start_time)}</td>
                          <td>{m.url ? <a href={m.url} target="_blank" rel="noreferrer">{m.name}</a> : m.name}</td>
                          <td>{m.open}</td>
                          <td>{m.close}</td>
                          <td>{m.home_price}</td>
                          <td>{m.away_price}</td>
                          <td>{m.half_score}</td>
                          <td>{m.full_score}</td>
                          <td className={o.cls}>{o.label}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="alert alert-warning">{t('similar.noMatch')}</div>
          )}
        </>
      )}
    </div>
  );
}

export default SimilarEvents;
